import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import { api, fmtUSD, API, getToken } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Search, Download, RefreshCw, Library, Upload } from "lucide-react";
import { toast } from "sonner";

const SUPER_COLORS = {
  Procedure: "bg-blue-50 text-blue-800 border-blue-200",
  Personnel: "bg-violet-50 text-violet-800 border-violet-200",
  Site: "bg-amber-50 text-amber-800 border-amber-200",
  "Pass-through": "bg-slate-50 text-slate-700 border-slate-200",
  Unknown: "bg-slate-50 text-slate-500 border-slate-200",
};

const SOURCE_COLORS = {
  external: "bg-emerald-50 text-emerald-800 border-emerald-200",
  blended: "bg-indigo-50 text-indigo-800 border-indigo-200",
  internal: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function RateCard() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [filterSuper, setFilterSuper] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async () => {
    const [r, s] = await Promise.all([
      api.get("/rate-card"),
      api.get("/rate-card/summary"),
    ]);
    setRows(r.data.rows || []);
    setSummary(s.data);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const refresh = useCallback(() => {
    setRefreshing(true);
    api.post("/rate-card/refresh")
      .then(({ data }) => {
        toast.success(`Rate card refreshed — ${data.total} items, ${data.internal_canonicals} from internal CTAs.`);
        return fetchAll();
      })
      .catch((e) => toast.error(e?.message || "Refresh failed"))
      .finally(() => setRefreshing(false));
  }, []);

  const loading = summary === null;

  const filtered = useMemo(() => {
    let f = rows;
    if (filterSuper) f = f.filter((r) => r.super_category === filterSuper);
    if (filterSource) f = f.filter((r) => r.source === filterSource);
    if (q && q.length >= 2) {
      const ql = q.toLowerCase();
      f = f.filter((r) =>
        (r.canonical_name || "").toLowerCase().includes(ql) ||
        (r.raw_samples || []).some((s) => (s || "").toLowerCase().includes(ql)) ||
        (r.sponsors || []).some((s) => (s || "").toLowerCase().includes(ql))
      );
    }
    return f;
  }, [rows, q, filterSuper, filterSource]);

  const fileRef = useRef(null);
  const [seeding, setSeeding] = useState(false);

  const onSeedFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setSeeding(true);
    const tid = toast.loading(`Seeding from ${f.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = getToken();
      const r = await fetch(`${API}/rate-card/seed-from-xlsx`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try { msg = (await r.json()).detail || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const data = await r.json();
      toast.success(`Seeded ${data.total} items (${data.blended} blended, ${data.external_only} external-only, ${data.internal_only} internal-only).`);
      await fetchAll();
    } catch (err) {
      toast.error(err.message || "Seed failed");
    } finally {
      toast.dismiss(tid);
      setSeeding(false);
    }
  };

  const exportCsv = () => {
    if (!filtered.length) return;
    const keys = ["super_category", "canonical_name", "p25", "median", "p75", "p80", "p90", "min", "max", "n", "source", "n_external", "n_internal"];
    const lines = [keys.join(",")];
    filtered.forEach((r) => {
      lines.push(keys.map((k) => {
        const v = r[k];
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "rate-card.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell
      title="Rate Card"
      subtitle="Single source of truth for clinical-trial line-item percentiles — external benchmark + your CTA history"
      actions={
        <div className="flex gap-2 items-center">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.xlsm"
            onChange={onSeedFile}
            className="hidden"
            data-testid="ratecard-seed-input"
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={seeding}
            data-testid="ratecard-seed-btn"
            title="Replace the rate card with a fresh seed from a benchmark Excel (Ridge layout)"
          >
            <Upload className="w-4 h-4 mr-1" />
            {seeding ? "Seeding..." : "Seed from Excel"}
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="ratecard-export-btn">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="ratecard-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh from CTAs"}
          </Button>
        </div>
      }
    >
      {/* KPI tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <KPI label="Total items" value={summary.total} />
          <KPI label="Procedure" value={summary.by_super_category.Procedure || 0} />
          <KPI label="Personnel" value={summary.by_super_category.Personnel || 0} />
          <KPI label="Site" value={summary.by_super_category.Site || 0} />
          <KPI label="Pass-through" value={summary.by_super_category["Pass-through"] || 0} />
        </div>
      )}

      {/* Filter + search */}
      <div className="bg-white border border-border rounded-md p-4 mb-4 shadow-sm" data-testid="ratecard-filters">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9 h-9"
              placeholder="Search: 'spirometry', 'IRB', 'pharmacy setup', 'SC fee', 'Pfizer'…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="ratecard-search"
            />
          </div>
          <FilterPill label="All categories" active={!filterSuper} onClick={() => setFilterSuper("")} />
          {["Procedure", "Personnel", "Site", "Pass-through"].map((sc) => (
            <FilterPill
              key={sc}
              label={sc}
              active={filterSuper === sc}
              onClick={() => setFilterSuper(filterSuper === sc ? "" : sc)}
              testid={`ratecard-filter-${sc.toLowerCase()}`}
            />
          ))}
          <span className="text-xs text-slate-300">·</span>
          <FilterPill label="All sources" active={!filterSource} onClick={() => setFilterSource("")} />
          {[
            { val: "external", label: "external only" },
            { val: "blended", label: "external + internal" },
            { val: "internal", label: "internal only" },
          ].map(({ val, label }) => (
            <FilterPill
              key={val}
              label={label}
              active={filterSource === val}
              onClick={() => setFilterSource(filterSource === val ? "" : val)}
            />
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-md shadow-sm overflow-auto" data-testid="ratecard-table">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Library className="w-4 h-4 text-[#0A4C95]" />
            <span className="text-sm font-semibold">{filtered.length} items</span>
            {q && <span className="text-xs text-slate-500">filtered by &quot;{q}&quot;</span>}
          </div>
        </div>
        <table className="w-full text-sm stryde-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5 w-[140px]">Super-category</th>
              <th className="text-left px-4 py-2.5">Line Item (canonical)</th>
              <th className="text-right px-4 py-2.5">P25</th>
              <th className="text-right px-4 py-2.5">Median</th>
              <th className="text-right px-4 py-2.5">P75</th>
              <th className="text-right px-4 py-2.5">P80</th>
              <th className="text-right px-4 py-2.5">P90</th>
              <th className="text-center px-4 py-2.5">n</th>
              <th className="text-center px-4 py-2.5">Source</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-10 text-slate-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-500">No matches</td></tr>}
            {filtered.map((r, i) => (
              <tr key={`${r.canonical_name}-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={`text-[10px] ${SUPER_COLORS[r.super_category] || SUPER_COLORS.Unknown}`}>
                    {r.super_category}
                  </Badge>
                </td>
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-slate-900">{r.canonical_name}</div>
                  {r.raw_samples && r.raw_samples.length > 0 && (
                    <div className="text-[10px] text-slate-400 mt-0.5 truncate max-w-md">
                      e.g. {r.raw_samples.slice(0, 2).join(" · ")}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right font-mono-fin text-slate-500 text-xs">{r.p25 != null ? fmtUSD(r.p25) : "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin">{r.median != null ? fmtUSD(r.median) : "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin font-semibold text-[#0A4C95]">{r.p75 != null ? fmtUSD(r.p75) : "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin">{r.p80 != null ? fmtUSD(r.p80) : "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin font-semibold text-emerald-700">{r.p90 != null ? fmtUSD(r.p90) : "—"}</td>
                <td className="px-4 py-2.5 text-center text-xs text-slate-500">
                  <div className="flex flex-col items-center gap-0.5">
                    <span>{r.n}</span>
                    {(r.n_external > 0 || r.n_internal > 0) && (
                      <span className="text-[9px] text-slate-400">
                        {r.n_external > 0 ? `ext ${r.n_external}` : ""}
                        {r.n_external > 0 && r.n_internal > 0 ? " · " : ""}
                        {r.n_internal > 0 ? `int ${r.n_internal}` : ""}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-center">
                  <div className="flex flex-col items-center gap-1">
                    {r.n_external > 0 && (
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200" title="Ridge Research benchmark Excel">
                        external
                      </Badge>
                    )}
                    {r.n_internal > 0 && (
                      <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-700 border-slate-200" title="Aggregated from your CTA library">
                        internal
                      </Badge>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

function KPI({ label, value }) {
  return (
    <div className="bg-white border border-border rounded-md p-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-mono-fin text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function FilterPill({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${
        active ? "bg-[#0A4C95] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`}
    >
      {label}
    </button>
  );
}
